import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import loadable from '@loadable/component';
import classNames from 'classnames';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { createSlug, parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { listingUnitLabel } from '../../util/editListingHelpers';
import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  TierSelector,
  FulfillmentSelector,
} from '../../components';
import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';

import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();
const ORDER_PANEL_SUBMIT_BUTTON_ID = 'orderPanelSubmitButton';

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    const formatted = formatMoney(intl, price);
    // Strip ".00" cents for whole-dollar amounts ($125 not $125.00)
    return typeof formatted === 'string' ? formatted.replace(/\.00$/, '') : formatted;
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    selectedTierKey,
  } = props;
  const { listingType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // RTE: when the listing has tiers, the header price should reflect the
  // tier the renter currently has selected — otherwise the base listing
  // price (always the first tier) disagrees with the booking breakdown
  // that's computed from the selected tier.
  const tiers = publicData?.tiers || null;
  const selectedTierPriceAmount =
    tiers && selectedTierKey && tiers[selectedTierKey]?.pricePerDay;
  const effectivePrice =
    selectedTierPriceAmount
      ? new Money(selectedTierPriceAmount, price.currency)
      : price;

  // Show all-in price (base + 15% customer fee) — matches Airbnb pattern
  const allInAmount = Math.round(effectivePrice.amount * 1.15);
  const allInPrice = new Money(allInAmount, effectivePrice.currency);
  const { formattedPrice, priceTitle } = priceData(allInPrice, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(allInPrice, intl)}</span>
  );
  // Canonical price quantifier (e.g. "per day", "per person") — derived
  // from the listing's pricing model, not the booking-process unitType.
  const unitLabel = listingUnitLabel(publicData);
  const pricePerUnit = <span className={css.perUnit}>{unitLabel}</span>;

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>{unitLabel}</div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
      </p>
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  // RTE: Tier and fulfillment state (can be externally controlled)
  const [internalTierKey, setInternalTierKey] = useState(null);
  const [internalFulfillment, setInternalFulfillment] = useState('pickup');
  // v1: No third-party delivery quotes — provider handles delivery directly

  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showListingImage,
    externalTierKey,
    onExternalTierChange,
    externalFulfillment,
    onExternalFulfillmentChange,
    externalDeliveryAddress,
    // Slot rendered inside the mobile modal between the tier selector
    // and the booking forms, hidden on desktop. The listing page passes
    // its <SectionFulfillmentOptions /> here so the mobile booking
    // popup includes the pickup/delivery + address-entry step that's
    // otherwise only available inline on the listing page above the
    // panel — letting the renter complete the whole flow without
    // dismissing the popup.
    mobileExtrasContent,
    // When true, the embedded mobile sticky CTA (.openOrderForm) is
    // suppressed -- callers (currently the listing detail page) supply
    // their own. Avoids two stacked sticky bars on mobile.
    hideMobileCTA = false,
    // Phase 2 (listing detail): host attribution moved out of the booking
    // card into the page's host header, and the numeric review rating is
    // surfaced next to the price.
    hideAuthor = false,
    reviewRating = null,
    reviewCount = 0,
  } = props;

  // Resolve tier/guest/fulfillment state: external props when provided, otherwise internal
  const isExternalTier = externalTierKey !== undefined && onExternalTierChange !== undefined;
  const selectedTierKey = isExternalTier ? externalTierKey : internalTierKey;
  const setSelectedTierKey = isExternalTier ? onExternalTierChange : setInternalTierKey;

  const isExternalFulfillment =
    externalFulfillment !== undefined && onExternalFulfillmentChange !== undefined;
  const selectedFulfillment = isExternalFulfillment ? externalFulfillment : internalFulfillment;
  const setSelectedFulfillment = isExternalFulfillment
    ? onExternalFulfillmentChange
    : setInternalFulfillment;

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', priceVariants, startTimeInterval } =
    publicData || {};

  // RTE: Extract tier and fulfillment data from publicData
  const tiers = publicData.tiers || null;
  const fulfillment = publicData.fulfillment || [];

  // RTE scheduling rules — read from publicData with kit defaults so
  // existing listings without the block keep the prior behavior. The
  // date picker uses these to enforce lead time and post-booking buffer
  // days. Multi-booking (calendar stays open after a confirmed booking)
  // is enforced server-side via the listing's availabilityPlan seats
  // count, set in EditListingDetailsPanel — nothing for the picker to
  // do here when it's true.
  const scheduling = publicData.scheduling || {};
  const leadTime = scheduling.leadTime || '48h';
  const timeBetweenBookings =
    scheduling.multipleBookingsPerDay === true
      ? 'none'
      : (scheduling.timeBetweenBookings || 'none');
  // DISABLED v0.14: Instant Book deferred to post-launch
  // const isInstantBook = publicData?.instantBookEnabled === true;
  const tierKeys = tiers ? Object.keys(tiers) : [];
  const isRTEListing = !!tiers && tierKeys.length > 0;


  // Auto-select first tier if not yet selected (only for internal state)
  useEffect(() => {
    if (isRTEListing && !isExternalTier && !internalTierKey) {
      setInternalTierKey(tierKeys[0]);
    }
  }, [isRTEListing, tierKeys.length]); // eslint-disable-line

  // Auto-select fulfillment method based on listing options
  useEffect(() => {
    if (fulfillment.length === 1) {
      setSelectedFulfillment(fulfillment[0]);
    }
  }, [fulfillment.length]); // eslint-disable-line

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation;

  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;

  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  // RTE: Wrap onFetchTransactionLineItems to inject tier + fulfillment
  // into orderData so the server line-items engine has the context it
  // needs to resolve the per-tier price and apply delivery fees.
  const handleFetchLineItems = isRTEListing
    ? params => {
        const enhancedParams = {
          ...params,
          orderData: {
            ...params.orderData,
            tierKey: selectedTierKey,
            deliveryMethod: selectedFulfillment,
          },
        };
        return onFetchTransactionLineItems(enhancedParams);
      }
    : onFetchTransactionLineItems;

  // Inject the selected fulfillment method into form submit values so the
  // checkout page knows whether to ask for a service address. The booking
  // form itself no longer has a fulfillment field (selector lives in
  // SectionFulfillmentOptions on the listing page).
  // Block the booking request if delivery or setup is selected but
  // the renter hasn't picked an address on the listing page.
  const fulfillmentNeedsAddress =
    selectedFulfillment === 'delivery' || selectedFulfillment === 'setup';
  const deliveryAddressRequired =
    isRTEListing && fulfillmentNeedsAddress && !externalDeliveryAddress;

  const wrappedOnSubmit = values => {
    if (deliveryAddressRequired) {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('Please enter a service address before requesting to book.');
      }
      return;
    }
    const extras = isRTEListing
      ? {
          // tierKey carries the renter-selected package tier through to
          // the server's line-item resolver. Without it, server falls
          // back to the listing's base price (= first tier = basic) and
          // both renter and host get the wrong amount.
          tierKey: selectedTierKey,
          deliveryMethod: selectedFulfillment,
          // Carry the structured event/delivery address through to checkout
          // so it pre-fills the delivery-address fields there (no re-entry).
          // city/state/zip come from the ZIP-centroid lookup on the listing
          // page; the renter can still edit any of it at checkout.
          ...(fulfillmentNeedsAddress && externalDeliveryAddress?.address
            ? {
                deliveryAddress: externalDeliveryAddress.address,
                deliveryOrigin: externalDeliveryAddress.origin,
                deliveryCity: externalDeliveryAddress.city,
                deliveryState: externalDeliveryAddress.state,
                deliveryZip: externalDeliveryAddress.zip,
              }
            : {}),
        }
      : {};
    return onSubmit({ ...values, ...extras });
  };

  const sharedProps = {
    lineItemUnitType,
    onSubmit: wrappedOnSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems: handleFetchLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    deliveryAddressRequired,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        scrollLayerClassName={css.modalScrollLayer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => {
          closeOrderModal(history, location);
          document.getElementById(ORDER_PANEL_SUBMIT_BUTTON_ID)?.focus();
        }}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
          {/* Audit item 11: the mobile modal is the primary booking path
              (sticky bar → here) but showed no price at all — the renter
              could book without ever seeing what the day costs. Tier-aware
              all-in price, same source as the desktop panel. */}
          <div className={css.modalPriceRow}>
            <PriceMaybe
              price={price}
              publicData={publicData}
              validListingTypes={validListingTypes}
              intl={intl}
              marketplaceCurrency={marketplaceCurrency}
              selectedTierKey={selectedTierKey}
            />
          </div>
        </div>

        {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )}

        {!hideAuthor && (
          <div className={css.author}>
            <AvatarSmall user={author} className={css.providerAvatar} />
            <span className={css.providerNameLinked}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
            </span>
            <span className={css.providerNamePlain}>
              <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
            </span>
          </div>
        )}

        {/* RTE: Tier selector (skip when externally managed) */}
        {isRTEListing && tiers && tierKeys.length > 1 && !isExternalTier && (
          <TierSelector
            tiers={tiers}
            selectedTierKey={selectedTierKey}
            onSelectTier={setSelectedTierKey}
            currency={marketplaceCurrency}
          />
        )}

        {/* Audit item 11: when the listing page manages tier state, the
            page-level TierSelector never appears in this modal — a renter
            arriving via the mobile sticky bar could book without ever
            seeing the Deluxe option. Render the same controlled selector
            here, modal-only (hidden at --viewportLarge where the panel is
            an inline sidebar next to the page-level selector). */}
        {isRTEListing && tiers && tierKeys.length > 1 && isExternalTier && (
          <div className={css.modalOnlyTier}>
            <TierSelector
              tiers={tiers}
              selectedTierKey={selectedTierKey}
              onSelectTier={setSelectedTierKey}
              currency={marketplaceCurrency}
            />
          </div>
        )}

        {/* Mobile extras slot — rendered inside the modal on mobile/tablet
            only. Listing page passes <SectionFulfillmentOptions /> here
            so the renter can pick pickup/delivery and enter their
            delivery address without dismissing the popup. */}
        {mobileExtrasContent ? (
          <div className={css.mobileExtras}>{mobileExtrasContent}</div>
        ) : null}

        {/* DISABLED v0.14: Instant Book deferred to post-launch */}
        {/* {isInstantBook && <InstantBookBadge isInstantBook={true} className={css.instantBookBadge} />} */}

        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : showBookingFixedDurationForm ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingDatesForm ? (
          <>
            <BookingDatesForm
              seatsEnabled={seatsEnabled}
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              finePrintComponent={SubmitFinePrint}
              externalRefetchKey={`${selectedTierKey}|${selectedFulfillment}`}
              leadTime={leadTime}
              timeBetweenBookings={timeBetweenBookings}
              {...priceVariantsMaybe}
              {...sharedProps}
            />
            {/* Fulfillment selector removed — shown in SectionFulfillmentOptions on listing page */}
          </>
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={onContactUser}
            {...sharedProps}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
          />
        ) : showNegotiationForm ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : showRequestQuoteForm ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}

        {/* Secondary CTA: message host without booking. Only surfaced for paid
            processes (booking/purchase/negotiation) — inquiry-only listings
            already send a message as their primary action. */}
        {onContactUser && !isInquiry && !isOwnListing && !isClosed ? (
          <button
            type="button"
            className={css.messageHostLink}
            onClick={onContactUser}
          >
            <FormattedMessage
              id="OrderPanel.messageHostLink"
              defaultMessage="Message host with questions"
            />
          </button>
        ) : null}
      </ModalInMobile>
      {hideMobileCTA ? null : (
        <div className={css.openOrderForm}>
          {/* DISABLED v0.14: Instant Book deferred to post-launch */}
          {/* <InstantBookBadge
            isInstantBook={publicData?.instantBookEnabled === true}
            className={css.instantBookBadge}
          />
          <div className={css.instantBookInfo}>
            {publicData?.instantBookEnabled === true && (
              <FormattedMessage id="OrderPanel.instantBookInfo" />
            )}
          </div> */}
          <div className={css.priceRow}>
            <PriceMaybe
              price={price}
              publicData={publicData}
              validListingTypes={validListingTypes}
              intl={intl}
              marketplaceCurrency={marketplaceCurrency}
              selectedTierKey={selectedTierKey}
              showCurrencyMismatch
            />
            {reviewRating != null && (
              <span className={css.orderRating}>
                <span className={css.orderRatingStar} aria-hidden="true">★</span>
                {reviewRating.toFixed(2)}
                <span className={css.orderRatingCount}>({reviewCount})</span>
              </span>
            )}
          </div>

          {isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="OrderPanel.closedListingButtonText" />
            </div>
          ) : (
            <PrimaryButton
              id={ORDER_PANEL_SUBMIT_BUTTON_ID}
              onClick={handleSubmit(
                isOwnListing,
                isClosed,
                showInquiryForm || showNegotiationForm,
                onSubmit,
                history,
                location
              )}
              disabled={isOutOfStock}
            >
              {isBooking ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
              ) : isOutOfStock ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
              ) : isPurchase ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              ) : showNegotiationForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
              ) : showRequestQuoteForm ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
              ) : (
                <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
              )}
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPanel;
